import React, { useEffect, useMemo, useState } from "react";
import { Paper, Typography, TextField, Select, MenuItem, Button, Grid } from "@mui/material";
import { listIndustries } from "../../../services/catalog";
import { updateIndustry } from "../../../services/shops";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { getTenantSettings, updateTenantSettings, TenantSettings } from "../../../services/settings";

const locales = ["en-US", "en-GB", "fr-FR", "de-DE", "es-ES", "pt-PT", "pt-BR"]; 
const timezones = ["UTC", "America/New_York", "Europe/London", "Africa/Harare", "Asia/Dubai", "Asia/Kolkata"]; 
const currencies = [
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "ZWL", symbol: "$" },
  { code: "ZAR", symbol: "R" },
];

export default function GeneralSettingsPage() {
  useProtectedRoute();
  const { token, role } = useAuth();
  const canEdit = role === "ADMIN" || role === "MANAGER";

  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [industryKey, setIndustryKey] = useState<string>("electronics");
  const [industries, setIndustries] = useState<Array<{ key: string; name: string }>>([]);

  useEffect(() => {
    (async () => {
      if (!token) return;
      try {
        setError(null);
        const s = await getTenantSettings(token);
        // Fill missing defaults defensively (for older tenants)
        const merged: TenantSettings = {
          locale: s.locale || "en-US",
          timezone: s.timezone || "UTC",
          currency: {
            code: s.currency?.code || "USD",
            symbol: s.currency?.symbol || "$",
            decimalSeparator: s.currency?.decimalSeparator || ".",
            thousandSeparator: s.currency?.thousandSeparator || ",",
          },
          numbering: {
            ticket: {
              prefix: s.numbering?.ticket?.prefix || "T-",
              padding: s.numbering?.ticket?.padding ?? 5,
              next: s.numbering?.ticket?.next ?? 1,
            },
            invoice: {
              prefix: s.numbering?.invoice?.prefix || "INV-",
              padding: s.numbering?.invoice?.padding ?? 5,
              next: s.numbering?.invoice?.next ?? 1,
            },
            purchase: {
              prefix: s.numbering?.purchase?.prefix || "PO-",
              padding: s.numbering?.purchase?.padding ?? 5,
              next: s.numbering?.purchase?.next ?? 1,
            },
          },
        } as TenantSettings;
        setSettings(merged);
        // Load industries and current tenant industry
        try {
          const list = await listIndustries();
          setIndustries(list);
        } catch {}
        try {
          const me = await api.get('/shops/me', { headers: { Authorization: `Bearer ${token}` } });
          setIndustryKey(me?.data?.industryKey || 'electronics');
        } catch {}
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load settings");
      }
    })();
  }, [token]);

  const preview = useMemo(() => {
    if (!settings) return { ticket: "", invoice: "" };
    const pad = (n: number, p: number) => String(n).padStart(p, "0");
    const t = settings.numbering?.ticket;
    const i = settings.numbering?.invoice;
    const ticket = `${(t?.prefix ?? "T-")}${pad(t?.next ?? 1, t?.padding ?? 5)}`;
    const invoice = `${(i?.prefix ?? "INV-")}${pad(i?.next ?? 1, i?.padding ?? 5)}`;
    return { ticket, invoice };
  }, [settings]);

  const updateField = (path: string, value: any) => {
    if (!settings) return;
    const next = { ...settings } as any;
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]] = { ...cur[keys[i]] };
    cur[keys[keys.length - 1]] = value;
    setSettings(next);
  };

  const save = async () => {
    if (!token || !settings) return;
    try {
      setSaving(true);
      setOk(null); setError(null);
      const updated = await updateTenantSettings(token, settings);
      setSettings(updated);
      setOk("Saved");
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const saveIndustry = async () => {
    if (!token) return;
    try {
      setSaving(true);
      setOk(null); setError(null);
      await updateIndustry(token, industryKey);
      setOk("Industry updated and defaults seeded");
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to update industry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <Typography variant="h5" fontWeight={900} gutterBottom>General Settings</Typography>

      {error && (
        <Paper elevation={0} className="p-3 mb-4 rounded-xl" sx={{ border: '1px solid', borderColor: 'error.main', bgcolor: 'rgba(239,68,68,0.08)' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}
      {ok && (
        <Paper elevation={0} className="p-3 mb-4 rounded-xl" sx={{ border: '1px solid', borderColor: 'success.main', bgcolor: 'rgba(16,185,129,0.08)' }}>
          <Typography color="success.main">{ok}</Typography>
        </Paper>
      )}

      {!settings ? (
        <Paper elevation={0} className="p-4 rounded-2xl" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
          Loading…
        </Paper>
      ) : (
        <>
          <Paper elevation={0} className="p-4 rounded-2xl mb-6" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={800} gutterBottom>Industry</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Select fullWidth size="small" value={industryKey} onChange={(e)=>setIndustryKey(String(e.target.value))} disabled={!canEdit}>
                  {industries.map((i)=> <MenuItem key={i.key} value={i.key}>{i.name}</MenuItem>)}
                </Select>
              </Grid>
              <Grid item xs={12} md="auto">
                <Button onClick={saveIndustry} disabled={!canEdit || saving} variant="contained">{saving ? 'Saving…' : 'Save Industry'}</Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} className="p-4 rounded-2xl mb-6" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={800} gutterBottom>Locale & Currency</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Select fullWidth size="small" value={settings.locale} onChange={(e)=>updateField('locale', e.target.value)} disabled={!canEdit}>
                  {locales.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                </Select>
              </Grid>
              <Grid item xs={12} md={4}>
                <Select fullWidth size="small" value={settings.timezone} onChange={(e)=>updateField('timezone', e.target.value)} disabled={!canEdit}>
                  {timezones.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </Grid>
              <Grid item xs={12} md={4}>
                <Select fullWidth size="small" value={settings.currency.code} onChange={(e)=>{
                  const code = e.target.value as string; 
                  const cur = currencies.find(c=>c.code===code);
                  updateField('currency.code', code);
                  if (cur) updateField('currency.symbol', cur.symbol);
                }} disabled={!canEdit}>
                  {currencies.map(c => <MenuItem key={c.code} value={c.code}>{c.code} ({c.symbol})</MenuItem>)}
                </Select>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField size="small" label="Symbol" value={settings.currency.symbol} onChange={(e)=>updateField('currency.symbol', e.target.value)} fullWidth disabled={!canEdit} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField size="small" label="Decimal" value={settings.currency.decimalSeparator} onChange={(e)=>updateField('currency.decimalSeparator', e.target.value)} fullWidth disabled={!canEdit} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField size="small" label="Thousand" value={settings.currency.thousandSeparator} onChange={(e)=>updateField('currency.thousandSeparator', e.target.value)} fullWidth disabled={!canEdit} />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} className="p-4 rounded-2xl mb-6" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={800} gutterBottom>Numbering</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography fontWeight={700} gutterBottom>Ticket</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField size="small" label="Prefix" value={settings.numbering.ticket.prefix} onChange={(e)=>updateField('numbering.ticket.prefix', e.target.value)} fullWidth disabled={!canEdit} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField size="small" type="number" label="Padding" value={settings.numbering.ticket.padding} onChange={(e)=>updateField('numbering.ticket.padding', Number(e.target.value))} fullWidth disabled={!canEdit} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField size="small" type="number" label="Next" value={settings.numbering.ticket.next} onChange={(e)=>updateField('numbering.ticket.next', Number(e.target.value))} fullWidth disabled={!canEdit} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography fontWeight={700} gutterBottom>Invoice</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField size="small" label="Prefix" value={settings.numbering.invoice.prefix} onChange={(e)=>updateField('numbering.invoice.prefix', e.target.value)} fullWidth disabled={!canEdit} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField size="small" type="number" label="Padding" value={settings.numbering.invoice.padding} onChange={(e)=>updateField('numbering.invoice.padding', Number(e.target.value))} fullWidth disabled={!canEdit} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField size="small" type="number" label="Next" value={settings.numbering.invoice.next} onChange={(e)=>updateField('numbering.invoice.next', Number(e.target.value))} fullWidth disabled={!canEdit} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Typography sx={{ mt: 1.5 }} color="text.secondary">
              Preview — Ticket: <b>{preview.ticket}</b> &nbsp; Invoice: <b>{preview.invoice}</b>
            </Typography>
          </Paper>

          <div className="flex justify-end">
            <Button onClick={save} disabled={!canEdit || saving} variant="contained">{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      )}
    </div>
  );
}
